import { useCallback, useState } from 'react';

import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  collectVersions,
  OnFarmImplantRequestError,
  type OnFarmImplantSessionEmbryo,
  useAddOnFarmImplantQueue,
  useRecordOnFarmImplantFlushDiscard,
} from '@/services/on-farm-implant';
import { useToast } from '@/services/toast';

export type FlushConfirmAction = 'queue' | 'discard';

type FlushEmbryoActionDialogProps = {
  action: FlushConfirmAction;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  sessionId: string;
  embryos: OnFarmImplantSessionEmbryo[];
  onDone: () => void;
};

const KEYS: Record<FlushConfirmAction, string> = {
  queue: 'onFarmImplant.flush.queue',
  discard: 'onFarmImplant.flush.discard',
};

export const FlushEmbryoActionDialog = ({
  action,
  open,
  onOpenChange,
  sessionId,
  embryos,
  onDone,
}: FlushEmbryoActionDialogProps) => {
  const { t } = useTranslation();
  const toast = useToast();

  const [problems, setProblems] = useState<string[]>([]);

  const addToQueue = useAddOnFarmImplantQueue();
  const discard = useRecordOnFarmImplantFlushDiscard();
  const isPending = addToQueue.isPending || discard.isPending;

  const keyPrefix = KEYS[action];

  const handleOpenChange = useCallback(
    (nextOpen: boolean) => {
      if (!nextOpen) setProblems([]);
      onOpenChange(nextOpen);
    },
    [onOpenChange],
  );

  const handleConfirm = useCallback(async () => {
    if (embryos.length === 0) return;

    setProblems([]);

    try {
      const count =
        action === 'queue'
          ? (
              await addToQueue.mutateAsync({
                sessionId,
                sourceType: 'flush',
                input: {
                  items: embryos.map((embryo) => ({
                    sessionEmbryoId: embryo.sessionEmbryoId,
                    transferStrawNumber: null,
                  })),
                  versions: collectVersions(embryos),
                },
              })
            ).totalQueued
          : (
              await discard.mutateAsync({
                sessionId,
                input: {
                  items: embryos.map((embryo) => ({
                    sessionEmbryoId: embryo.sessionEmbryoId,
                  })),
                  versions: collectVersions(embryos),
                },
              })
            ).totalDiscarded;

      handleOpenChange(false);
      onDone();

      toast.show({
        severity: 'success',
        summary: t(`${keyPrefix}.success`, { count }),
      });
    } catch (error) {
      if (error instanceof OnFarmImplantRequestError && error.problems.length) {
        setProblems(error.problems);
        return;
      }

      console.error(`Failed to ${action} flush embryos:`, error);
      toast.show({ severity: 'error', summary: t(`${keyPrefix}.failed`) });
    }
  }, [
    action,
    addToQueue,
    discard,
    embryos,
    handleOpenChange,
    keyPrefix,
    onDone,
    sessionId,
    t,
    toast,
  ]);

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="md:max-w-2xl">
        <DialogHeader>
          <DialogTitle>{t(`${keyPrefix}.title`)}</DialogTitle>
          <DialogDescription>{t(`${keyPrefix}.description`)}</DialogDescription>
        </DialogHeader>

        <DialogBody className="flex flex-col gap-3">
          {problems.length > 0 && (
            <div
              role="alert"
              className="rounded-md bg-red-50 p-3 text-xs text-red-700"
            >
              <ul className="list-inside list-disc">
                {problems.map((problem) => (
                  <li key={problem}>{problem}</li>
                ))}
              </ul>
            </div>
          )}

          <table className="w-full text-left text-xs">
            <thead>
              <tr className="text-gray-500">
                <th className="py-1 font-medium">
                  {t('onFarmImplant.flush.embryo')}
                </th>
                <th className="py-1 font-medium">
                  {t('onFarmImplant.flush.freshFrozen')}
                </th>
                <th className="py-1 font-medium">
                  {t('onFarmImplant.flush.straw')}
                </th>
                <th className="py-1 font-medium">
                  {t('onFarmImplant.flush.stage')}
                </th>
                <th className="py-1 font-medium">
                  {t('onFarmImplant.flush.grade')}
                </th>
              </tr>
            </thead>
            <tbody>
              {embryos.map((embryo) => (
                <tr key={embryo.sessionEmbryoId} className="border-t">
                  <td className="py-1.5">{embryo.embryoName ?? '—'}</td>
                  <td className="py-1.5">
                    {embryo.freshFrozen === 'Fresh'
                      ? t('onFarmImplant.flush.fresh')
                      : embryo.freshFrozen === 'Frozen'
                        ? t('onFarmImplant.flush.frozen')
                        : '—'}
                  </td>
                  <td className="py-1.5">{embryo.frozenStrawNumber ?? '—'}</td>
                  <td className="py-1.5">{embryo.stage ?? '—'}</td>
                  <td className="py-1.5">{embryo.grade ?? '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </DialogBody>

        <DialogFooter>
          <Button variant="secondary" onClick={() => handleOpenChange(false)}>
            {t('common.cancel')}
          </Button>
          <Button
            variant={action === 'discard' ? 'destructive' : 'default'}
            onClick={handleConfirm}
            disabled={embryos.length === 0 || isPending}
          >
            {isPending && <i className="pi pi-spin pi-spinner" />}
            {t(`${keyPrefix}.confirm`)}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
